import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { money, QUOTE_STATUS, computeProfit } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, FileText, Loader2, AlertTriangle } from "lucide-react";

function StatusPill({ status }) {
  const s = QUOTE_STATUS[status] || QUOTE_STATUS.draft;
  return <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${s.cls}`}>{s.label}</span>;
}

export default function Quotes() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canProfit = can("view_profit");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes", { params: { q, date_from: from, date_to: to, status: status === "all" ? "" : status } });
      setItems(data);
    } finally { setLoading(false); }
  }, [q, from, to, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader
        title="Teklifler"
        subtitle="Tüm teklifleri görüntüleyin, arayın, filtreleyin ve durumlarını takip edin."
        action={
          <Link to="/teklifler/yeni">
            <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" data-testid="quotes-new-btn"><Plus className="w-4 h-4 mr-2" />Yeni Teklif</Button>
          </Link>
        }
      />

      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Teklif no veya müşteri ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="quote-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="quote-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="draft">Taslak</SelectItem>
            <SelectItem value="sent">Gönderildi</SelectItem>
            <SelectItem value="approved">Onaylandı</SelectItem>
            <SelectItem value="rejected">Reddedildi</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-center">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" data-testid="quote-date-from" />
          <span className="text-slate-400 text-sm">-</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" data-testid="quote-date-to" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-100 rounded-2xl card-soft">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Teklif bulunamadı.</p>
        </Card>
      ) : (
        <Card className="border-slate-100 rounded-2xl card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-bold uppercase text-xs tracking-wider">Teklif No</TableHead>
                  <TableHead className="font-bold uppercase text-xs tracking-wider">Müşteri</TableHead>
                  <TableHead className="font-bold uppercase text-xs tracking-wider hidden md:table-cell">Tarih</TableHead>
                  <TableHead className="font-bold uppercase text-xs tracking-wider hidden lg:table-cell">Hazırlayan</TableHead>
                  <TableHead className="font-bold uppercase text-xs tracking-wider text-right">Tutar</TableHead>
                  {canProfit && <TableHead className="font-bold uppercase text-xs tracking-wider text-right hidden sm:table-cell">Kâr / Oran</TableHead>}
                  <TableHead className="font-bold uppercase text-xs tracking-wider text-center">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((qt) => (
                  <TableRow key={qt.id} className="cursor-pointer" onClick={() => navigate(`/teklifler/${qt.id}`)} data-testid={`quote-row-${qt.id}`}>
                    <TableCell className="font-semibold text-rose-600">{qt.quote_number}</TableCell>
                    <TableCell>{qt.customer_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500">{(qt.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-500">{qt.prepared_by_name}</TableCell>
                    <TableCell className="text-right font-heading font-bold">{money(qt.grand_total, qt.currency)}</TableCell>
                    {canProfit && (
                    <TableCell className="text-right hidden sm:table-cell">
                      {(() => {
                        const p = computeProfit(qt);
                        return p.calculable ? (
                          <span className={p.profit >= 0 ? "text-emerald-600 font-medium" : "text-rose-600 font-medium"} data-testid={`quote-profit-${qt.id}`}>
                            {money(p.profit, qt.currency)}<span className="text-xs text-slate-400 ml-1">%{p.rate.toFixed(1)}</span>
                          </span>
                        ) : (
                          <span className="text-amber-600 text-xs inline-flex items-center gap-1" title="Alış/satış fiyatı belirsiz ürünler var" data-testid={`quote-profit-${qt.id}`}><AlertTriangle className="w-3 h-3" />Belirsiz</span>
                        );
                      })()}
                    </TableCell>
                    )}
                    <TableCell className="text-center"><StatusPill status={qt.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
